import { createElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActivityIndicator, Text } from 'react-native';

import { DialogButton } from '@/components/ui/Dialog.web';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';
import {
  DATABASE_COLUMN_TYPES,
  columnTypeLabel,
  defaultImportMapping,
  type ImportColumnMappingChoice,
  type ImportMappingInput,
  type ImportTable,
} from '@couplespace/core/databases';

const IMPORTABLE_TYPES = DATABASE_COLUMN_TYPES.filter(
  (t) => t !== 'relation' && t !== 'rollup',
);

type ColumnMappingState = {
  sourceName: string;
  target: 'skip' | 'new' | string;
  finalType: string;
};

type ImportDialogProps = {
  open: boolean;
  table: ImportTable | null;
  existingColumns: DatabaseColumnDefinition[];
  onClose: () => void;
  onConfirm: (mapping: ImportMappingInput) => Promise<void>;
};

function buildInitialMapping(
  table: ImportTable,
  existingColumns: DatabaseColumnDefinition[],
): { titleSourceName: string | null; columns: ColumnMappingState[] } {
  const defaults = defaultImportMapping(table, existingColumns);
  return {
    titleSourceName: defaults.titleSourceName ?? table.columns[0]?.sourceName ?? null,
    columns: defaults.columns.map((c) => ({
      sourceName: c.sourceName,
      target: c.target,
      finalType: c.finalType ?? 'text',
    })),
  };
}

export function ImportDialog({
  open,
  table,
  existingColumns,
  onClose,
  onConfirm,
}: ImportDialogProps) {
  const [mapping, setMapping] = useState<{
    titleSourceName: string | null;
    columns: ColumnMappingState[];
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && table) {
      setMapping(buildInitialMapping(table, existingColumns));
      setError(null);
    }
  }, [open, table, existingColumns]);

  if (!open || !table || !mapping || typeof document === 'undefined') {
    return null;
  }

  function setColumn(index: number, patch: Partial<ColumnMappingState>) {
    setMapping((prev) => {
      if (!prev) {
        return prev;
      }
      const next = [...prev.columns];
      next[index] = { ...next[index]!, ...patch };
      return { ...prev, columns: next };
    });
  }

  async function handleConfirm() {
    if (!mapping) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const columns: ImportColumnMappingChoice[] = mapping.columns.map((c) => ({
        sourceName: c.sourceName,
        target: c.target,
        finalType: c.finalType as ImportColumnMappingChoice['finalType'],
      }));
      await onConfirm({
        titleSourceName: mapping.titleSourceName,
        columns,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    createElement(
      'div',
      {
        className: 'fixed inset-0 z-[100000] flex items-center justify-center p-4',
        'data-cs-dialog': 'true',
        onMouseDown: (e: MouseEvent) => {
          if (e.target === e.currentTarget && !submitting) {
            onClose();
          }
        },
      },
      createElement(
        'div',
        {
          className:
            'flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-border bg-surface shadow-2xl',
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        createElement(
          'div',
          { className: 'border-b border-border px-4 py-3' },
          createElement(
            'h2',
            { className: 'text-base font-semibold text-fg' },
            `Import CSV — ${table.name}`,
          ),
          createElement(
            'p',
            { className: 'mt-1 text-sm text-fg-muted' },
            `${table.rows.length} row(s), ${table.columns.length} column(s).`,
          ),
        ),
        createElement(
          'div',
          { className: 'flex-1 overflow-auto px-4 py-3' },
          createElement(
            'label',
            { className: 'mb-3 flex flex-col gap-1 text-sm' },
            'Title column',
            createElement(
              'select',
              {
                className: 'cs-db-popover-select',
                value: mapping.titleSourceName ?? '',
                disabled: submitting,
                onChange: (e: Event) => {
                  const v = (e.target as HTMLSelectElement).value;
                  const nextTitle = v || null;
                  setMapping((prev) => {
                    if (!prev) {
                      return prev;
                    }
                    const columns = prev.columns.map((c) => {
                      if (nextTitle && c.sourceName === nextTitle) {
                        return { ...c, target: 'skip' as const };
                      }
                      if (
                        prev.titleSourceName &&
                        c.sourceName === prev.titleSourceName &&
                        c.target === 'skip'
                      ) {
                        return { ...c, target: 'new' as const };
                      }
                      return c;
                    });
                    return { titleSourceName: nextTitle, columns };
                  });
                },
              },
              createElement('option', { value: '' }, '(none)'),
              ...table.columns.map((c) =>
                createElement('option', { key: c.sourceName, value: c.sourceName }, c.sourceName),
              ),
            ),
          ),
          createElement(
            'table',
            { className: 'cs-db-import-map-table w-full text-sm' },
            createElement(
              'thead',
              null,
              createElement(
                'tr',
                null,
                createElement('th', { className: 'text-left py-1 pr-2' }, 'Source'),
                createElement('th', { className: 'text-left py-1 pr-2' }, 'Map to'),
                createElement('th', { className: 'text-left py-1 pr-2' }, 'Type'),
                createElement('th', { className: 'text-left py-1' }, 'Sample'),
              ),
            ),
            createElement(
              'tbody',
              null,
              ...mapping.columns.map((col, index) => {
                const source = table.columns.find((c) => c.sourceName === col.sourceName);
                const sample = source?.sampleValues[0] ?? '';
                const isTitleSource =
                  Boolean(mapping.titleSourceName) &&
                  col.sourceName === mapping.titleSourceName;
                return createElement(
                  'tr',
                  { key: col.sourceName },
                  createElement('td', { className: 'py-1 pr-2 font-medium' }, col.sourceName),
                  createElement(
                    'td',
                    { className: 'py-1 pr-2' },
                    createElement(
                      'select',
                      {
                        className: 'cs-db-popover-select w-full',
                        value: isTitleSource ? 'skip' : col.target,
                        disabled: submitting || isTitleSource,
                        onChange: (e: Event) => {
                          const v = (e.target as HTMLSelectElement).value;
                          setColumn(index, {
                            target: v === 'skip' || v === 'new' ? v : v,
                          });
                        },
                      },
                      createElement('option', { value: 'new' }, 'New column'),
                      ...existingColumns
                        .filter((c) => c.type !== 'relation' && c.type !== 'rollup')
                        .map((c) =>
                          createElement('option', { key: c.id, value: c.id }, c.name),
                        ),
                      createElement('option', { value: 'skip' }, 'Skip'),
                    ),
                  ),
                  createElement(
                    'td',
                    { className: 'py-1 pr-2' },
                    createElement(
                      'select',
                      {
                        className: 'cs-db-popover-select w-full',
                        value: col.finalType,
                        disabled: submitting || col.target === 'skip',
                        onChange: (e: Event) => {
                          setColumn(index, {
                            finalType: (e.target as HTMLSelectElement).value,
                          });
                        },
                      },
                      ...IMPORTABLE_TYPES.map((t) =>
                        createElement('option', { key: t, value: t }, columnTypeLabel(t)),
                      ),
                    ),
                  ),
                  createElement(
                    'td',
                    { className: 'py-1 text-fg-muted truncate max-w-[12rem]' },
                    sample,
                  ),
                );
              }),
            ),
          ),
          error
            ? createElement(Text, { className: 'mt-2 text-sm text-danger' }, error)
            : null,
        ),
        createElement(
          'div',
          {
            className:
              'flex flex-row items-center justify-end gap-2 border-t border-border px-4 py-3',
          },
          submitting
            ? createElement(ActivityIndicator, { size: 'small' })
            : null,
          createElement(DialogButton, {
            label: 'Cancel',
            onPress: onClose,
            disabled: submitting,
          }),
          createElement(DialogButton, {
            label: submitting ? 'Importing…' : 'Import',
            variant: 'primary',
            disabled: submitting || table.rows.length === 0,
            onPress: () => void handleConfirm(),
          }),
        ),
      ),
    ),
    document.body,
  );
}
